import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { boundaryPartsToSessionInput } from 'utils/coordinateConversion';
import { getStoredPrescriptionId } from 'utils/prescriptionStorage';

import useApplication from 'hooks/useApplication';
import usePlanSession from 'hooks/usePlanSession';
import usePrescription from 'hooks/usePrescription';
import useSavePlan from 'hooks/useSavePlan';
import { useToast } from 'hooks/useToast';

import useMapData from 'pages/ApplicationDetailsPage/hooks/useMapData';
import useTranslation from 'pages/ApplicationDetailsPage/hooks/useTranslation';

import {
    DEBOUNCED_CONFIG_KEYS,
    DEBOUNCE_MS,
    DEFAULT_SECTION_OPEN_STATE,
    DEFAULT_SPRAY_SETTINGS,
    REPLAN_POLL_INTERVAL_MS,
    REPLAN_POLL_TIMEOUT_MS,
    SECTION_ADVANCED,
    SECTION_COVERAGE,
    SECTION_SPRAY,
    SQM_PER_HECTARE
} from './constants';
import type {
    ApiPrescriptionZone,
    ApplicationResponse,
    AttachPrescription,
    DetachPrescription,
    RoutePlanConfig,
    SectionOpenState,
    SpraySettings,
    UseDataParams,
    UseDataReturn
} from './types';

const isDebounceEligible = (partial: Partial<RoutePlanConfig>): boolean => {
    const keys = Object.keys(partial);

    return (
        keys.length > 0 &&
        keys.every(key => DEBOUNCED_CONFIG_KEYS.includes(key as keyof RoutePlanConfig))
    );
};

const deriveSpraySettings = ({
    application
}: {
    application: ApplicationResponse;
}): SpraySettings => {
    const settings = application.applicationSettings;

    if (!settings) {
        return DEFAULT_SPRAY_SETTINGS as SpraySettings;
    }

    return {
        applicationType: settings.applicationType,
        nozzleRatePercentage: settings.nozzleRatePercentage,
        overlapMeters: settings.overlapMeters,
        productCategory: settings.productCategory,
        rateKilogramsPerHectare: settings.rateKilogramsPerHectare,
        rateLitersPerHectare: settings.rateLitersPerHectare,
        speedMetersPerSecond: settings.speedMetersPerSecond,
        turntablePercentage: settings.turntablePercentage
    } as SpraySettings;
};

const useData = ({
    application,
    boundary,
    onClose,
    onPrescriptionChange,
    onSaved
}: UseDataParams): UseDataReturn => {
    const { SAVE_ERROR } = useTranslation();
    const { broadcastToast } = useToast();
    const { fetchApplication } = useApplication();
    const { fetchPrescription } = usePrescription();
    const {
        attachError,
        attachPrescription: attachPrescriptionRaw,
        config,
        createSession,
        detachPrescription: detachPrescriptionRaw,
        disconnect,
        error: sessionError,
        isAttachingPrescription,
        isConnecting,
        isDetachingPrescription,
        isReplanning,
        planResult,
        rateBounds,
        replanTimeMs,
        sessionId,
        updateConfig,
        zoneCount
    } = usePlanSession();
    const [prescriptionZones, setPrescriptionZones] = useState<ApiPrescriptionZone[] | null>(null);
    const { isSaving: localIsSaving, savePlan } = useSavePlan();

    const { boundary: boundaryLatLons, obstacles } = useMemo(
        () => boundaryPartsToSessionInput({ boundaryParts: boundary.boundaryParts }),
        [boundary.boundaryParts]
    );

    const { flightPaths } = useMapData({
        boundary: boundaryLatLons,
        draftBoundary: null,
        isEditingBoundary: false,
        obstacles,
        planResult
    });

    const [spraySettings, setSpraySettings] = useState<SpraySettings>(() =>
        deriveSpraySettings({ application })
    );
    const [sectionOpenState, setSectionOpenState] = useState<SectionOpenState>(
        DEFAULT_SECTION_OPEN_STATE as SectionOpenState
    );
    const [localError, setLocalError] = useState<string | null>(null);
    const [isSavingPlan, setIsSavingPlan] = useState<boolean>(false);
    const [isPickingHomePoint, setIsPickingHomePoint] = useState<boolean>(false);

    const debouncedPartialRef = useRef<Partial<RoutePlanConfig>>({});
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isReplanningRef = useRef(isReplanning);

    useEffect(() => {
        isReplanningRef.current = isReplanning;
    }, [isReplanning]);

    // Wrap attach / detach so the panel can capture the FE-local zones on
    // their way to the wire. usePlanSession only retains rateBounds + zoneCount
    // after the server ack, so we hold on to the raw zones here for the map
    // preview layer (Option A — sibling-Map prop threading).
    const attachPrescription: AttachPrescription = useCallback(
        params => {
            setPrescriptionZones(params.zones);
            attachPrescriptionRaw(params);
        },
        [attachPrescriptionRaw]
    );

    const detachPrescription: DetachPrescription = useCallback(() => {
        setPrescriptionZones(null);
        detachPrescriptionRaw();
    }, [detachPrescriptionRaw]);

    // Bubble the prescription state up to the page so the sibling Map can
    // render zone polygons. Fires on local zone changes (parse + finalize) and
    // on rateBounds changes (server ack of PrescriptionAttached / Detached).
    useEffect(() => {
        onPrescriptionChange({ bounds: rateBounds, zones: prescriptionZones });
    }, [onPrescriptionChange, prescriptionZones, rateBounds]);

    // On unmount, clear the page-level state so a stale prescription preview
    // does not survive panel teardown.
    useEffect(
        () => () => {
            onPrescriptionChange({ bounds: null, zones: null });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const flushDebouncedConfig = useCallback(() => {
        const partial = debouncedPartialRef.current;

        debouncedPartialRef.current = {};

        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }

        if (Object.keys(partial).length === 0) {
            return;
        }

        updateConfig({ config: partial });
    }, [updateConfig]);

    const waitForReplanIfNeeded = useCallback(async (): Promise<void> => {
        if (!isReplanningRef.current) {
            return;
        }

        const deadline = Date.now() + REPLAN_POLL_TIMEOUT_MS;

        while (isReplanningRef.current && Date.now() < deadline) {
            await new Promise<void>(resolve => setTimeout(resolve, REPLAN_POLL_INTERVAL_MS));
        }
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const initializeSession = async () => {
            if (isCancelled) {
                return;
            }

            await createSession({ boundary: boundaryLatLons, obstacles });
        };

        initializeSession();

        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundary.id]);

    const handleConfigChange = useCallback(
        ({ config: partial }: { config: Partial<RoutePlanConfig> }) => {
            if (!isDebounceEligible(partial)) {
                flushDebouncedConfig();
                updateConfig({ config: partial });

                return;
            }

            debouncedPartialRef.current = { ...debouncedPartialRef.current, ...partial };

            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            debounceTimeoutRef.current = setTimeout(() => flushDebouncedConfig(), DEBOUNCE_MS);
        },
        [flushDebouncedConfig, updateConfig]
    );

    const handleSpraySettingsChange = useCallback(
        ({ spraySettings: partial }: { spraySettings: Partial<SpraySettings> }) => {
            setSpraySettings(previous => ({ ...previous, ...partial }));
        },
        []
    );

    const handleCoverageToggle = useCallback(() => {
        setSectionOpenState(previous => ({
            ...previous,
            [SECTION_COVERAGE]: !previous[SECTION_COVERAGE]
        }));
    }, []);

    const handleAdvancedToggle = useCallback(() => {
        setSectionOpenState(previous => ({
            ...previous,
            [SECTION_ADVANCED]: !previous[SECTION_ADVANCED]
        }));
    }, []);

    const handleSprayToggle = useCallback(() => {
        setSectionOpenState(previous => ({
            ...previous,
            [SECTION_SPRAY]: !previous[SECTION_SPRAY]
        }));
    }, []);

    const handleHomePointPick = useCallback(() => {
        setIsPickingHomePoint(previous => !previous);
    }, []);

    const handleHomePointSelected = useCallback(
        (point: { lat: number; lon: number }) => {
            updateConfig({ config: { homePoint: point } });
            setIsPickingHomePoint(false);
        },
        [updateConfig]
    );

    const handleDiscard = useCallback(() => {
        disconnect();
        onClose();
    }, [disconnect, onClose]);

    // Noop replan used by PrescriptionSection after a successful attach. The
    // server does NOT auto-replan when a prescription is attached or detached
    // so we re-issue the current config via the existing updateConfig
    // pipeline (an empty partial is a valid noop on the wire).
    const handlePrescriptionReplan = useCallback(() => {
        updateConfig({ config: {} });
    }, [updateConfig]);

    // Auto-reattach a previously-created prescription on planner mount. The
    // shapefile-first flow stashes the prescription id in localStorage keyed
    // to the application, then navigates here with autoOpenPlanner: true.
    // On the first render where the plan session is live, rehydrate the
    // prescription from chemical-db-api and feed it through the existing
    // attach + replan pipeline.
    useEffect(() => {
        if (!application?.id) {
            return;
        }

        if (!sessionId) {
            return;
        }

        const storedId = getStoredPrescriptionId(application.id);

        if (!storedId) {
            return;
        }

        let cancelled = false;

        const run = async () => {
            const prescription = await fetchPrescription({ id: storedId });

            if (cancelled || !prescription) {
                return;
            }

            attachPrescription({
                gapPolicy: prescription.gapPolicy,
                rateUnit: prescription.rateUnit,
                zones: prescription.zones
            });
            handlePrescriptionReplan();
        };

        run();

        return () => {
            cancelled = true;
        };
    }, [
        application?.id,
        sessionId,
        attachPrescription,
        fetchPrescription,
        handlePrescriptionReplan
    ]);

    const handleSave = useCallback(async () => {
        if (isSavingPlan) {
            return;
        }

        setIsSavingPlan(true);

        try {
            flushDebouncedConfig();
            await waitForReplanIfNeeded();

            if (!application.id || !sessionId) {
                return;
            }

            const result = await savePlan({
                applicationId: application.id,
                sessionId,
                spraySettings
            });

            if (!result) {
                setLocalError(SAVE_ERROR);
                broadcastToast({ text: SAVE_ERROR, type: 'error' });

                return;
            }

            const updated = await fetchApplication({ id: application.id });

            disconnect();

            if (updated) {
                onSaved({ application: updated });
            } else {
                onClose();
            }
        } catch (saveError) {
            console.error(saveError);
            setLocalError(SAVE_ERROR);
            broadcastToast({ text: SAVE_ERROR, type: 'error' });
        } finally {
            setIsSavingPlan(false);
        }
    }, [
        SAVE_ERROR,
        application.id,
        broadcastToast,
        disconnect,
        fetchApplication,
        flushDebouncedConfig,
        isSavingPlan,
        onClose,
        onSaved,
        savePlan,
        sessionId,
        spraySettings,
        waitForReplanIfNeeded
    ]);

    useEffect(
        () => () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        },
        []
    );

    return {
        attachError,
        attachPrescription,
        boundaryAreaHectares:
            typeof boundary.areaSqM === 'number' ? boundary.areaSqM / SQM_PER_HECTARE : null,
        config,
        detachPrescription,
        error: localError ?? sessionError,
        flightPaths,
        handleAdvancedToggle,
        handleConfigChange,
        handleCoverageToggle,
        handleDiscard,
        handleHomePointPick,
        handleHomePointSelected,
        handlePrescriptionReplan,
        handleSave,
        handleSectionToggle: handleCoverageToggle,
        handleSpraySettingsChange,
        handleSprayToggle,
        isAttachingPrescription,
        isConnecting,
        isDetachingPrescription,
        isPickingHomePoint,
        isReplanning,
        isSaving: localIsSaving || isReplanning || isSavingPlan,
        planResult,
        rateBounds,
        replanTimeMs,
        sectionOpenState,
        sessionId,
        spraySettings,
        zoneCount
    } as UseDataReturn;
};

export default useData;
